import { useState, useEffect, useMemo, type FC } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { routes } from '@/lib/routes';
import {
  CabinetWorkspaceFrame,
  ScientificGpsBlock,
} from '@/features/cabinet/CabinetWorkspaceFrame';
import type {
  EditorCardId,
  EditorObject,
  EditorOverviewStats,
  EditorProfile,
  EditorTabKey,
  EditorWorkspaceSummary,
} from './types';
import {
  getEditorSummary,
  updateEditorObject,
  addEditorVersion,
  addEditorParticipant,
  addEditorApproval,
  runEditorTool,
  createEditorJointArticle,
  createEditorCalendarEvent,
  deleteEditorCalendarEvent,
} from './api';
import { EditorCabinetGrid } from './components/EditorCabinetGrid';
import { EditorWorkCenterOverlay } from './components/EditorWorkCenterOverlay';
import { EditorObjectDetailOverlay } from './components/EditorObjectDetailOverlay';
import { EditorDocToolsOverlay } from './components/EditorDocToolsOverlay';
import { EditorStatsOverlay } from './components/EditorStatsOverlay';
import { EditorJointArticlesOverlay } from './components/EditorJointArticlesOverlay';
import { EditorNotebookOverlay } from './components/EditorNotebookOverlay';
import { EditorCalendarOverlay } from './components/EditorCalendarOverlay';
import { SuperAiWorkspaceModal } from '@/features/super-ai';
import { CreateRequestEntry } from '@/features/request-gateway';
import { RoleAcademicConnectionsOverlay } from '@/features/communications';
import { FileHubPage } from '@/features/file-hub';

const FULL_PAGE_OVERLAYS: Array<EditorCardId | 'NOTEBOOK' | 'CALENDAR'> = [
  'B5_FILES',
  'NOTEBOOK',
  'CALENDAR',
  'ACADEMIC_CONNECTIONS',
  'B5_ARTICLES',
];



export const EditorPage: FC = () => {
  const { user } = useAuth();
  const { notify } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [summary, setSummary] = useState<EditorWorkspaceSummary | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<boolean>(false);
  const [activeOverlay, setActiveOverlay] = useState<
    EditorCardId | 'NOTEBOOK' | 'CALENDAR' | null
  >(null);
  const [selectedObjectCode, setSelectedObjectCode] = useState<string | null>(null);
  const [selectedObjectSourceTab, setSelectedObjectSourceTab] = useState<EditorTabKey>('ALL');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isAIModalOpen, setIsAIModalOpen] = useState<boolean>(false);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setLoadError(false);
      const data = await getEditorSummary();
      setSummary(data);
    } catch {
      setSummary(null);
      setLoadError(true);
      notify('Не удалось загрузить кабинет редактора', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const section = searchParams.get('section');
    const objectCodeParam = searchParams.get('object');
    const createParam = searchParams.get('create');
    const aiParam = searchParams.get('ai');

    if (objectCodeParam) {
      setSelectedObjectCode(objectCodeParam);
    }

    if (createParam === 'true') {
      setIsCreateModalOpen(true);
    }

    if (aiParam === 'true') {
      setIsAIModalOpen(true);
    }

    if (section) {
      const lower = section.toLowerCase();
      if (lower === 'requests' || lower === 'editor_work_center') {
        setActiveOverlay('EDITOR_WORK_CENTER');
      } else if (lower === 'tools' || lower === 'editor_doc_tools') {
        setActiveOverlay('EDITOR_DOC_TOOLS');
      } else if (lower === 'connections' || lower === 'academic_connections') {
        setActiveOverlay('ACADEMIC_CONNECTIONS');
      } else if (lower === 'stats' || lower === 'editor_stats') {
        setActiveOverlay('EDITOR_STATS');
      } else if (lower === 'files' || lower === 'b5_files') {
        setActiveOverlay('B5_FILES');
      } else if (lower === 'articles' || lower === 'b5_articles') {
        setActiveOverlay('B5_ARTICLES');
      } else if (lower === 'notebook') {
        setActiveOverlay('NOTEBOOK');
      } else if (lower === 'calendar') {
        setActiveOverlay('CALENDAR');
      }
    }
  }, [searchParams]);

  const handleOpenCard = (id: EditorCardId | 'NOTEBOOK' | 'CALENDAR') => {
    setActiveOverlay(id);
    setSearchParams({ section: id.toLowerCase() });
  };

  const handleCloseOverlay = () => {
    setActiveOverlay(null);
    setSearchParams({});
  };

  const handleOpenObjectDetail = (obj: EditorObject, sourceTab: EditorTabKey = 'ALL') => {
    setSelectedObjectCode(obj.object_code);
    setSelectedObjectSourceTab(sourceTab);
    setSearchParams({ object: obj.object_code });
  };

  const handleCloseObjectDetail = () => {
    setSelectedObjectCode(null);
    if (activeOverlay) {
      setSearchParams({ section: activeOverlay.toLowerCase() });
    } else {
      setSearchParams({});
    }
  };

  const handleBackFromObjectDetail = () => {
    setSelectedObjectCode(null);
    setActiveOverlay('EDITOR_WORK_CENTER');
    setSearchParams({ section: 'requests' });
  };

  const currentSelectedObject = useMemo(() => {
    if (!selectedObjectCode || !summary) return null;
    return (
      summary.objects.find((o) => o.object_code === selectedObjectCode) || null
    );
  }, [selectedObjectCode, summary]);

  const handleUpdateObjectStatus = async (
    id: string,
    status: string,
    nextStep?: string,
    actionNote?: string
  ) => {
    await updateEditorObject(id, {
      status,
      next_step: nextStep,
      action_note: actionNote,
    });
    await loadData();
  };

  const handleAddObjectVersion = async (
    objectId: string,
    versionNumber: string,
    title: string,
    changelog: string
  ) => {
    await addEditorVersion(objectId, {
      version_number: versionNumber,
      title,
      changelog,
    });
    await loadData();
  };

  const handleAddObjectParticipant = async (
    objectId: string,
    name: string,
    role: string,
    userCode: string
  ) => {
    await addEditorParticipant(objectId, {
      user_code: userCode,
      name,
      role,
    });
    await loadData();
  };

  const handleAddObjectApproval = async (
    objectId: string,
    stageName: string,
    decision: string,
    comment: string
  ) => {
    await addEditorApproval(objectId, {
      stage_name: stageName,
      decision,
      comment,
    });
    await loadData();
  };

  const stats: EditorOverviewStats = summary?.stats || {
    total_objects: 0,
    active_objects: 0,
    in_approval_objects: 0,
    completed_objects: 0,
    urgent_deadlines_count: 0,
    unread_messages_count: 0,
    total_requests: 0,
    in_work_requests: 0,
    breakdown_by_kind: {
      ARTICLE: 0,
      COAUTHOR: 0,
      EDITORIAL: 0,
      MATERIAL: 0,
      APPROVAL: 0,
      DONE: 0,
    },
    breakdown_by_status: {},
  };

  const profile: EditorProfile = summary?.profile || {
    id: '',
    user_id: user?.id || '',
    full_name: user ? `${user.firstName} ${user.lastName}` : '',
    user_code: '',
    position: '',
    academic_degree: '',
    phone: '',
    email: user?.email || '',
    organization: '',
    telegram: '',
    preferred_contact_method: 'TELEGRAM',
    notes: null,
  };

  if (isLoading && !summary) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh] space-y-4">
        <div className="w-12 h-12 border-4 border-cyan-500/30 border-t-cyan-400 rounded-full animate-spin" />
        <span className="text-sm text-slate-400 font-medium">
          Загрузка кабинета редактора проекта...
        </span>
      </div>
    );
  }

  if (!summary || loadError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh] space-y-4">
        <span className="text-sm text-slate-400 font-medium">
          Не удалось загрузить данные кабинета редактора
        </span>
        <button
          type="button"
          onClick={() => void loadData()}
          className="px-4 py-2 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-bold transition"
        >
          Повторить
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid="editor-page"
      className="w-full px-4 sm:px-6 lg:px-8 py-5 space-y-5 text-slate-100 role-cabinet-page"
    >
      {activeOverlay == null || !FULL_PAGE_OVERLAYS.includes(activeOverlay) ? (
      <CabinetWorkspaceFrame
        links={{
          notebook: `${routes.editor}?section=notebook`,
          calendar: `${routes.editor}?section=calendar`,
          meetings: `${routes.editor}?section=calendar`,
          connections: `${routes.editor}?section=academic_connections`,
          alliances: `${routes.editor}?section=b5_articles`,
          qa: routes.portalSection('instructions'),
          support: routes.portalSection('contacts'),
        }}
      >
      <div className="space-y-2.5" aria-label="Срочные уведомления редактора">
        <div className="flex items-center justify-between px-4 py-2.5 rounded-xl bg-gradient-to-r from-red-950/70 via-rose-950/50 to-slate-900 border border-red-500/40 text-xs shadow-lg backdrop-blur-md">
          <div className="flex items-center gap-3 overflow-hidden">
            <span className="px-2 py-0.5 rounded bg-red-600 font-extrabold text-[10px] tracking-wider uppercase text-white shadow">
              СРОЧНО
            </span>
            <span className="font-semibold text-rose-200 truncate">
              Требует внимания: Мои заявки:
            </span>
            <span className="text-slate-300 truncate hidden md:inline">
              ED-ART-041 (Срочная проверка цитирований, ост. 2 дня) · ED-MAT-052 (Нехватка исходных данных, ост. 1 день)
            </span>
          </div>
          <button
            onClick={() => handleOpenCard('EDITOR_WORK_CENTER')}
            className="shrink-0 ml-3 px-3 py-1 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-rose-300 border border-red-500/40 font-semibold text-[11px] transition"
          >
            Открыть заявки →
          </button>
        </div>

        <div className="flex items-center justify-between px-4 py-2.5 rounded-xl bg-gradient-to-r from-amber-950/60 via-orange-950/40 to-slate-900 border border-amber-500/40 text-xs shadow-lg backdrop-blur-md">
          <div className="flex items-center gap-3 overflow-hidden">
            <span className="px-2 py-0.5 rounded bg-amber-500 font-extrabold text-[10px] tracking-wider uppercase text-slate-950 shadow">
              ВНИМАНИЕ
            </span>
            <span className="font-semibold text-amber-200 truncate">
              Документы и инструменты:
            </span>
            <span className="text-slate-300 truncate hidden md:inline">
              Доступен обновлённый шаблон паспорта исследования и чек-лист оформления по Vancouver
            </span>
          </div>
          <button
            onClick={() => handleOpenCard('EDITOR_DOC_TOOLS')}
            className="shrink-0 ml-3 px-3 py-1 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/40 font-semibold text-[11px] transition"
          >
            Открыть инструменты →
          </button>
        </div>

        <div className="flex items-center justify-between px-4 py-2.5 rounded-xl bg-gradient-to-r from-cyan-950/60 via-blue-950/40 to-slate-900 border border-cyan-500/40 text-xs shadow-lg backdrop-blur-md">
          <div className="flex items-center gap-3 overflow-hidden">
            <span className="px-2 py-0.5 rounded bg-cyan-500 font-extrabold text-[10px] tracking-wider uppercase text-slate-950 shadow">
              ОЖИДАЮТ
            </span>
            <span className="font-semibold text-cyan-200 truncate">
              Совместные статьи и Файлы:
            </span>
            <span className="text-slate-300 truncate hidden md:inline">
              ED-JA-004 — согласование порядка авторов · 3 новых экспертных заключения в файловом архиве
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0 ml-3">
            <button
              onClick={() => handleOpenCard('B5_ARTICLES')}
              className="px-2.5 py-1 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 border border-cyan-500/40 font-semibold text-[11px] transition"
            >
              Статьи
            </button>
            <button
              onClick={() => handleOpenCard('B5_FILES')}
              className="px-2.5 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 font-semibold text-[11px] transition"
            >
              Файлы
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-5 flex flex-col">
          <header
            className="role-hero"
            style={{ ['--role-accent' as string]: '#41d2ff' }}
          >
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-tr from-cyan-500 via-blue-600 to-indigo-500 flex items-center justify-center text-lg font-black text-white shrink-0">
                СЖ
              </div>
              <div className="min-w-0">
                <small>
                  {profile.user_code} · Редактор проекта · верифицирован
                </small>
                <h2 className="truncate">{profile.full_name}</h2>
                <p>
                  {profile.academic_degree} · {profile.organization}
                </p>
              </div>
            </div>
            <div className="role-hero-actions">
              <button
                type="button"
                className="role-btn"
                onClick={() => setIsAIModalOpen(true)}
              >
                Super AI
              </button>
              <button
                type="button"
                className="role-btn primary"
                onClick={() => setIsCreateModalOpen(true)}
              >
                + Создать заявку
              </button>
            </div>
          </header>

          <div className="role-statgrid">
            <button
              type="button"
              className="role-stat"
              style={{ ['--a' as string]: '#e2ad3a' }}
              onClick={() => handleOpenCard('EDITOR_WORK_CENTER')}
            >
              <strong>{stats.total_objects}</strong>
              <span>Всего заявок</span>
            </button>
            <button
              type="button"
              className="role-stat"
              style={{ ['--a' as string]: '#41d2ff' }}
              onClick={() => handleOpenCard('EDITOR_WORK_CENTER')}
            >
              <strong>{stats.active_objects}</strong>
              <span>В работе</span>
            </button>
            <button
              type="button"
              className="role-stat"
              style={{ ['--a' as string]: '#ff7a64' }}
              onClick={() => handleOpenCard('CALENDAR')}
            >
              <strong>{stats.urgent_deadlines_count}</strong>
              <span>Дедлайны 7 дней</span>
            </button>
            <button
              type="button"
              className="role-stat"
              style={{ ['--a' as string]: '#9e80ff' }}
              onClick={() => handleOpenCard('ACADEMIC_CONNECTIONS')}
            >
              <strong>{stats.unread_messages_count}</strong>
              <span>Сообщения</span>
            </button>
          </div>

          <div className="owner-hub-top-actions">
            <button
              type="button"
              className="owner-hub-ai-action"
              onClick={() => setIsAIModalOpen(true)}
            >
              <span className="spark" aria-hidden="true">
                ✦
              </span>
              <span>
                <strong>Super AI: Научный ассистент редакции</strong>
                <small>Аудит структуры, Scopus, Vancouver, протоколы</small>
              </span>
              <b>Открыть →</b>
            </button>
            <button
              type="button"
              className="owner-hub-create"
              onClick={() => setIsCreateModalOpen(true)}
            >
              + Создать заявку
            </button>
          </div>

          <EditorCabinetGrid
            stats={stats}
            onSelectCard={handleOpenCard}
            activeCard={activeOverlay as EditorCardId}
          />

          <ScientificGpsBlock openTo={`${routes.editor}?section=editor_stats`} />
      </div>
      </CabinetWorkspaceFrame>
      ) : null}

      <EditorWorkCenterOverlay
        isOpen={activeOverlay === 'EDITOR_WORK_CENTER'}
        onClose={handleCloseOverlay}
        objects={summary?.objects || []}
        onSelectObject={(obj, sourceTab) => {
          handleOpenObjectDetail(obj, sourceTab);
        }}
      />

      <EditorObjectDetailOverlay
        isOpen={selectedObjectCode !== null}
        onClose={handleCloseObjectDetail}
        onBack={handleBackFromObjectDetail}
        object={currentSelectedObject}
        sourceTab={selectedObjectSourceTab}
        onUpdateStatus={handleUpdateObjectStatus}
        onAddVersion={handleAddObjectVersion}
        onAddParticipant={handleAddObjectParticipant}
        onAddApproval={handleAddObjectApproval}
        onOpenConnections={() => handleOpenCard('ACADEMIC_CONNECTIONS')}
        onOpenFiles={() => handleOpenCard('B5_FILES')}
      />

      <EditorDocToolsOverlay
        isOpen={activeOverlay === 'EDITOR_DOC_TOOLS'}
        onClose={handleCloseOverlay}
        templates={summary?.templates || []}
        objects={summary?.objects || []}
        onOpenFiles={() => handleOpenCard('B5_FILES')}
        onRunTool={async (payload) => {
          return await runEditorTool(payload);
        }}
      />

      <RoleAcademicConnectionsOverlay
        isOpen={activeOverlay === 'ACADEMIC_CONNECTIONS'}
        onClose={handleCloseOverlay}
        roleContext="PROJECT_EDITOR"
        roleTitle="Редактор проекта"
      />

      <EditorStatsOverlay
        isOpen={activeOverlay === 'EDITOR_STATS'}
        onClose={handleCloseOverlay}
        stats={stats}
        objects={summary?.objects || []}
        onDrillDown={(tab, selectedObj) => {
          setActiveOverlay('EDITOR_WORK_CENTER');
          if (selectedObj) {
            handleOpenObjectDetail(selectedObj, tab);
          }
        }}
      />

      {activeOverlay === 'B5_FILES' ? (
        <FileHubPage onClose={handleCloseOverlay} onBack={handleCloseOverlay} />
      ) : null}

      <EditorJointArticlesOverlay
        isOpen={activeOverlay === 'B5_ARTICLES'}
        onClose={handleCloseOverlay}
        articles={summary?.joint_articles || []}
        onCreateArticle={async (payload) => {
          const created = await createEditorJointArticle(payload);
          await loadData();
          return created;
        }}
      />

      <EditorNotebookOverlay
        isOpen={activeOverlay === 'NOTEBOOK'}
        onClose={handleCloseOverlay}
      />

      <EditorCalendarOverlay
        isOpen={activeOverlay === 'CALENDAR'}
        onClose={handleCloseOverlay}
        events={summary?.calendar_events || []}
        onCreateEvent={async (payload) => {
          const created = await createEditorCalendarEvent(payload);
          await loadData();
          return created;
        }}
        onDeleteEvent={async (id) => {
          await deleteEditorCalendarEvent(id);
          await loadData();
        }}
      />

      <CreateRequestEntry
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        activeRole="PROJECT_EDITOR"
        roleLabel="Редактор проекта"
      />

      <SuperAiWorkspaceModal
        isOpen={isAIModalOpen}
        onClose={() => setIsAIModalOpen(false)}
        activeRole="PROJECT_EDITOR"
      />
    </div>
  );
};

export default EditorPage;
